import { AsyncPipe, NgClass } from '@angular/common';
import { AfterViewChecked, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { Attachment } from '@ninety/platform/models/_shared/attachment';
import { MeetingMessageAction } from '@ninety/platform/models/meetings/meeting-message-action';
import { extractValueFromStore } from '@ninety/platform/services';
import { FeatureFlagKeys, selectCurrentUser, selectFeatureFlag, selectLanguage } from '@ninety/platform/state';
import { LoggingActions } from '@ninety/platform/state/app-logging/app-logging.actions';
import { TerraDividerModule, TerraIconModule } from '@ninety/terra';
import { FileAttachmentsComponent } from '@ninety/ui/core';
import {
  NinetyRichTextEditorComponent,
  NinetyTextEditorBackendDirective,
  withoutUnfinishedUploads,
} from '@ninety/ui/legacy/components/ninety-rich-text-editor';
import { TextEditorComponent } from '@ninety/ui/legacy/shared/index';
import { CreateDialogService } from '@ninety/web/pages/layouts/services/create-dialog.service';

import { MeetingService } from '../_shared/services/meeting.service';
import { MeetingNotesActions } from '../_state/meetings.actions';
import { MeetingsStateSelectors } from '../_state/meetings.selectors';

@Component({
  selector: 'ninety-meeting-notes',
  templateUrl: './meeting-notes.component.html',
  styleUrls: ['./meeting-notes.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    FormsModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    TerraIconModule,
    TextEditorComponent,
    NinetyRichTextEditorComponent,
    NinetyTextEditorBackendDirective,
    TerraDividerModule,
    FileAttachmentsComponent,
  ],
})
export class MeetingNotesComponent implements OnInit, AfterViewChecked, OnDestroy {
  readonly meetingService = inject(MeetingService);
  private readonly store = inject(Store);
  private readonly createDialogService = inject(CreateDialogService);

  expanded = true;
  private readonly subscriptions = new Subscription();
  protected updateMeetingNotes$ = new Subject<string>();

  /**
   * Set once the RTE mounts (flag resolves true) so `ngAfterViewChecked` can arm the sync guard off
   * its `initialized` observable — see that field's own doc comment for why `currentMeeting$` alone
   * isn't a reliable trigger for this.
   */
  private rte?: NinetyRichTextEditorComponent; // set by the `notesEditor` @ViewChild setter
  private rteInitSubscription?: Subscription;

  /** Input for the attachments component. See the selector for why it is memoized. */
  protected readonly meetingIdAndAttachments = this.store.selectSignal(
    MeetingsStateSelectors.selectMeetingIdAndAttachments
  );

  private meetingId: string | undefined;
  private lastEmittedNotes: string | undefined;
  private lastSavedNotes: string | undefined;

  /**
   * True for one macrotask after either (a) `currentMeeting$` emits (realtime broadcasts — see
   * `MeetingService.setCurrentMeeting()` → `_currentMeeting.next()`) or (b) the RTE itself reports
   * `initialized` (covers the flag resolving true — and thus the RTE mounting fresh — with no
   * accompanying `currentMeeting$` emission to hang a guard off of). Guards `onNotesChanged` against
   * the RTE's `contentChanged` echo when Froala normalizes HTML on an external `[ngModel]`
   * write-back or its own initial content set — a raw string-equality check can't reliably catch
   * this, since the shared `FroalaEditorDirective`'s inbound/outbound sanitization is asymmetric
   * (compares the raw value it was given against Froala's sanitized round-trip), so an echo can
   * legitimately differ from what was set. Timing-based, like `ItemDescriptionComponent`'s
   * `isSyncingEditor`, rather than content-based.
   */
  private isSyncingEditor = false;
  private syncTimeoutId: ReturnType<typeof setTimeout> | undefined;

  /** Notes held back from `updateMeetingNotes$` because an image/file upload was still in flight —
   * see `onNotesChanged`'s doc comment. */
  private pendingNotesSave: string | undefined;
  private imageUploadsPendingSub?: Subscription;
  /** Bound per editor instance, so a re-created editor (flag/`@if`) still releases held notes. */
  @ViewChild(NinetyRichTextEditorComponent)
  private set notesEditor(editor: NinetyRichTextEditorComponent | undefined) {
    if (editor === this.rte) return; // query re-run, same editor
    // A re-created editor never settles the old one's upload — release held notes without it.
    this.releaseHeldNotes(true);
    this.rte = editor;
    this.imageUploadsPendingSub?.unsubscribe();
    this.imageUploadsPendingSub = editor?.imageUploadsPending$.subscribe(pending => {
      if (!pending) this.releaseHeldNotes();
    });
  }

  private readonly language$ = this.store.select(selectLanguage);
  language = extractValueFromStore(this.store, selectLanguage);

  private readonly user$ = this.store.select(selectCurrentUser);
  user = extractValueFromStore(this.store, selectCurrentUser);

  protected readonly richTextEditorEnabled$ = this.store.select(
    selectFeatureFlag(FeatureFlagKeys.enabledNinetyRichTextEditorMeetings)
  );

  ngOnInit() {
    this.subscriptions.add(
      this.meetingService.currentMeeting$.pipe(filter(m => !!m?._id)).subscribe(m => {
        this.meetingId = m._id;
        this.beginEditorSync();
      })
    );
    this.subscriptions.add(this.language$.subscribe(l => (this.language = l)));
    this.subscriptions.add(this.user$.subscribe(u => (this.user = u)));

    this.subscriptions.add(
      this.updateMeetingNotes$
        .pipe(
          tap(notes => {
            this.lastEmittedNotes = notes;
            this.meetingService.hasDirtyNotes = true;
          }),
          debounceTime(1000),
          distinctUntilChanged(),
          switchMap(notes =>
            this.meetingService.updateCurrentMeeting({ notes }).pipe(
              tap(() => {
                this.lastSavedNotes = notes;
                this.meetingService.hasDirtyNotes = false;
              }),
              switchMap(() =>
                this.meetingService.broadcastMessage({
                  messageType: 'meeting',
                  document: {
                    action: MeetingMessageAction.Update,
                    meeting: { notes },
                    emitterId: this.user?._id,
                  },
                })
              ),
              catchError((e: unknown) => {
                this.store.dispatch(
                  LoggingActions.warn({
                    log: {
                      message: 'meeting_notes_broadcast_failed',
                      data: { meetingId: this.meetingId },
                      error: e,
                    },
                  })
                );
                return EMPTY;
              })
            )
          )
        )
        .subscribe()
    );

    // On reconnect, immediately flush any unsaved notes to DB before the service's
    // reconnect fetch can overwrite the TextEditor with stale DB content.
    this.subscriptions.add(
      this.meetingService.connectionEstablished$
        .pipe(
          filter(
            () =>
              this.lastEmittedNotes !== undefined &&
              this.lastEmittedNotes !== this.lastSavedNotes &&
              !!this.meetingId &&
              !!this.user &&
              !this.user.isObserver
          ),
          switchMap(() => this.saveNotesNow(this.lastEmittedNotes, 'meeting_notes_reconnect_flush_failed'))
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.createDialogService.createOpenEvent$.subscribe({
        next: () => {
          if (this.expanded) {
            this.expanded = false;
          }
        },
      })
    );
  }

  /**
   * Arms the sync guard off the RTE's own `initialized` observable once it mounts (flag resolves
   * true), and tears the subscription down when it unmounts (flag resolves false / component
   * destroyed). `@ViewChild` into a structurally-conditional (`@if`) element only settles after view
   * checks, hence hooking this from `ngAfterViewChecked` rather than `ngAfterViewInit`.
   */
  ngAfterViewChecked(): void {
    if (this.rte && !this.rteInitSubscription) {
      this.rteInitSubscription = this.rte.initialized.pipe(filter(Boolean)).subscribe(() => this.beginEditorSync());
    } else if (!this.rte && this.rteInitSubscription) {
      this.rteInitSubscription.unsubscribe();
      this.rteInitSubscription = undefined;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.syncTimeoutId);
    this.rteInitSubscription?.unsubscribe();
    this.imageUploadsPendingSub?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.saveUnsavedNotesOnClose();
  }

  /** Notes panel closed: save directly what the torn-down autosave hadn't — held notes (minus the unfinished upload)
   * or the latest notes still inside the debounce. Not tied to this component's lifetime, so the request outlives it. */
  private saveUnsavedNotesOnClose(): void {
    let notes: string;
    if (this.pendingNotesSave !== undefined) {
      notes = withoutUnfinishedUploads(this.pendingNotesSave);
      this.pendingNotesSave = undefined;
      if (this.meetingService.currentMeeting) this.meetingService.currentMeeting.notes = notes;
    } else if (this.lastEmittedNotes !== undefined && this.lastEmittedNotes !== this.lastSavedNotes) {
      notes = this.lastEmittedNotes;
    } else {
      return;
    }
    this.saveNotesNow(notes, 'meeting_notes_close_flush_failed').subscribe();
  }

  /** Persist + broadcast outside the autosave debounce (reconnect flush, close flush). */
  private saveNotesNow(notes: string, failureMessage: string): Observable<unknown> {
    return this.meetingService.updateCurrentMeeting({ notes }).pipe(
      tap(() => {
        this.lastSavedNotes = notes;
        this.meetingService.hasDirtyNotes = false;
      }),
      switchMap(() =>
        this.meetingService
          .broadcastMessage({
            messageType: 'meeting',
            document: {
              action: MeetingMessageAction.Update,
              meeting: { notes },
              emitterId: this.user?._id,
            },
          })
          .pipe(catchError(() => EMPTY))
      ),
      catchError((e: unknown) => {
        this.store.dispatch(
          LoggingActions.warn({
            log: { message: failureMessage, data: { meetingId: this.meetingId }, error: e },
          })
        );
        return EMPTY;
      })
    );
  }

  /**
   * Opens a one-macrotask window where `onNotesChanged` treats any `contentChanged` emission as an
   * echo of the external write we just applied, not a user edit. See `isSyncingEditor`'s doc comment
   * for why timing, not content comparison, is the reliable signal here.
   */
  private beginEditorSync(): void {
    this.isSyncingEditor = true;
    clearTimeout(this.syncTimeoutId);
    this.syncTimeoutId = setTimeout(() => (this.isSyncingEditor = false));
  }

  /**
   * RTE branch — replaces the legacy editor's two-way [(text)] write-back, then autosaves.
   * Observers can't edit ([canEdit]="!user?.isObserver" disables the toolbar) — reject here too,
   * as a code-level backstop against an observer's client publishing a programmatic-write echo
   * that slips past the timing guard, rather than relying solely on the RTE's own visual state.
   *
   * Defers the autosave while an image/file upload is still in flight (`rte.hasPendingImageUploads`)
   * — saving now would persist a still-resolving `blob:`/placeholder src, permanently losing the
   * image once that URL expires. Held notes are replayed once uploads clear.
   */
  protected onNotesChanged(notes: string): void {
    if (this.isSyncingEditor || !this.meetingService.currentMeeting || this.user?.isObserver) {
      return;
    }
    this.meetingService.currentMeeting.notes = notes;
    if (this.rte?.hasPendingImageUploads) {
      this.pendingNotesSave = notes;
      // Dirty, so a reconnect refetch doesn't replace the editor with stale notes while these are held.
      this.meetingService.hasDirtyNotes = true;
      return;
    }
    this.updateMeetingNotes$.next(notes);
  }

  /** `withoutUnfinished` when the editor holding the upload is gone. */
  private releaseHeldNotes(withoutUnfinished = false): void {
    if (this.pendingNotesSave === undefined) return;
    const notes = withoutUnfinished ? withoutUnfinishedUploads(this.pendingNotesSave) : this.pendingNotesSave;
    this.pendingNotesSave = undefined;
    // onNotesChanged already wrote the unfiltered notes here — keep the model in step with what's saved.
    if (withoutUnfinished && this.meetingService.currentMeeting) this.meetingService.currentMeeting.notes = notes;
    this.updateMeetingNotes$.next(notes);
  }

  protected onAttachmentAdded(attachment: Attachment): void {
    this.store.dispatch(MeetingNotesActions.attachmentAdded({ attachment }));
  }

  protected onAttachmentRemoved(attachment: Attachment): void {
    this.store.dispatch(MeetingNotesActions.attachmentRemoved({ attachment }));
  }
}
